/**
 * CONTRATO DO RESET DA BASE — espelho do motor (ST-Operations-os#141).
 *
 * O motor mudou o reset para duas chamadas e a tela ficou no contrato antigo
 * (senha no header `x-demo-reset-secret` + `{ confirmPhrase }`): todo reset
 * voltava 400 `reset_invalid_request` e a tela dizia só "Falha ao resetar".
 *
 * Fonte no motor (axiomgo-engine):
 * - `src/api/v1/admin/reset-authorization.ts` → `INPUT`
 *   `z.object({ password: z.string().min(1).max(1024), attemptId: z.string().min(1).max(64) }).strict()`
 * - `src/api/v1/admin/reset-demo.ts` → `POST /reset-demo/reauth-attempt` devolve `{ attemptId, expiresAt }`
 * - `src/api/v1/admin/reset-access.ts` (`rejectReset`) → recusa `{ code, message, retryAfterSeconds? }`
 * - `src/api/v1/admin/reset-password-setup.ts` → `POST /reset-demo/reset-password` define/troca a
 *   SENHA DE RESET do inquilino (20/10/2026). O `password` do reset é ESSA senha, não a do login:
 *   conta que entra pelo Google não tem senha no Clerk e ficava sem reset.
 *
 * O pacote `@rochadenis/axiomgo-contracts` ainda não tem esquema de reset; até
 * ter, este arquivo é o espelho e `reset-contract.test.ts` + o teste do
 * `api.resetTotal` com o motor dublado travam a divergência deste lado.
 */
import { z } from 'zod'

export const RESET_REAUTH_ATTEMPT_PATH = '/api/v1/admin/reset-demo/reauth-attempt'
export const RESET_EXECUTION_PATH = '/api/v1/admin/reset-demo'
export const RESET_PASSWORD_PATH = '/api/v1/admin/reset-demo/reset-password'

/** Mínimo da senha de reset — o mesmo `RESET_PASSWORD_MIN_LENGTH` do motor. */
export const RESET_PASSWORD_MIN_LENGTH = 8

/**
 * Corpo ESTRITO do `POST /reset-demo/reset-password`: só `newPassword` para definir a primeira vez;
 * `currentPassword` + `attemptId` (do reauth-attempt) para trocar.
 */
export const resetPasswordInputSchema = z
  .object({
    newPassword: z.string().min(RESET_PASSWORD_MIN_LENGTH).max(1024),
    currentPassword: z.string().min(1).max(1024).optional(),
    attemptId: z.string().min(1).max(64).optional(),
  })
  .strict()
export type ResetPasswordInput = z.infer<typeof resetPasswordInputSchema>

/** Corpo ESTRITO do `POST /reset-demo` — qualquer campo a mais é 400 no motor. */
export const resetExecutionInputSchema = z
  .object({
    password: z.string().min(1).max(1024),
    attemptId: z.string().min(1).max(64),
  })
  .strict()
export type ResetExecutionInput = z.infer<typeof resetExecutionInputSchema>

/** Resposta do `POST /reset-demo/reauth-attempt`: nonce de 15 min ligado à sessão. */
export const resetReauthAttemptSchema = z.object({
  attemptId: z.string().min(1).max(64),
  expiresAt: z.string().min(1),
})
export type ResetReauthAttempt = z.infer<typeof resetReauthAttemptSchema>

/** Todo `code` que o motor manda numa recusa do reset, com o status HTTP de cada um. */
export const RESET_REFUSAL_STATUS = {
  reset_unavailable: 404,
  reset_authentication_required: 401,
  reset_admin_required: 403,
  reset_authentication_unavailable: 403,
  reset_invalid_request: 400,
  reauth_required: 403,
  password_incorrect: 403,
  reset_password_not_set: 409,
  reset_password_already_set: 409,
  reset_password_invalid: 400,
  reset_verification_unavailable: 503,
  reset_maintenance_unavailable: 503,
  reset_in_progress: 409,
  reset_rate_limited: 429,
} as const
export type ResetRefusalCode = keyof typeof RESET_REFUSAL_STATUS

const RESET_REFUSAL_CODES = Object.keys(RESET_REFUSAL_STATUS) as [
  ResetRefusalCode,
  ...ResetRefusalCode[],
]

export const resetRefusalSchema = z.object({
  code: z.enum(RESET_REFUSAL_CODES),
  message: z.string(),
  retryAfterSeconds: z.number().int().positive().optional(),
})
export type ResetRefusal = z.infer<typeof resetRefusalSchema>

/** Lê a recusa estruturada do motor; `undefined` quando o corpo não é desse contrato. */
export function parseResetRefusal(body: unknown): ResetRefusal | undefined {
  const parsed = resetRefusalSchema.safeParse(body)
  return parsed.success ? parsed.data : undefined
}

/** Public correlation only: never the reauthentication nonce or reset password. */
export const resetOperationIdSchema = z.uuid()
export const resetOperationOutcomeSchema = z.object({
  operationId: resetOperationIdSchema,
  timestamp: z.iso.datetime({ offset: true }),
  phase: z.string().min(1).max(64),
  wiped: z.boolean().nullable(),
  retrySafe: z.boolean(),
  success: z.boolean(),
})
export const resetOperationStatusSchema = z.object({
  operationId: resetOperationIdSchema,
  inProgress: z.boolean(),
  lastOutcome: resetOperationOutcomeSchema.nullable(),
})
export type ResetOperationStatus = z.infer<typeof resetOperationStatusSchema>
